'use client';

/**
 * Service Goals Tracker Component
 *
 * Manages goals for a service referral:
 * - Add/edit goals
 * - Track progress percentage
 * - Update goal status and outcomes
 *
 * Used in case workspace for goal-based service tracking.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Plus,
  Target,
  Calendar,
  Edit2,
  Check,
  X,
  Loader2,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  MinusCircle,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  serviceReferralsAPI,
  ServiceGoal,
  ServiceGoalCreate,
  ServiceGoalUpdate,
  GoalStatus,
  GoalOutcome,
} from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceGoalsTrackerProps {
  referralId: number;
  onGoalUpdated?: (goal: ServiceGoal) => void;
  readOnly?: boolean;
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const getGoalStatuses = (t: (key: string, fallback: string) => string): { value: GoalStatus; label: string; color: string; icon: React.ReactNode }[] => [
  { value: 'active', label: t('serviceReferrals.goals.status.active', 'Active'), color: 'blue', icon: <TrendingUp className="w-4 h-4" /> },
  { value: 'achieved', label: t('serviceReferrals.goals.status.achieved', 'Achieved'), color: 'green', icon: <CheckCircle className="w-4 h-4" /> },
  { value: 'partially_achieved', label: t('serviceReferrals.goals.status.partiallyAchieved', 'Partially Achieved'), color: 'amber', icon: <AlertCircle className="w-4 h-4" /> },
  { value: 'discontinued', label: t('serviceReferrals.goals.status.discontinued', 'Discontinued'), color: 'red', icon: <MinusCircle className="w-4 h-4" /> },
  { value: 'modified', label: t('serviceReferrals.goals.status.modified', 'Modified'), color: 'purple', icon: <Edit2 className="w-4 h-4" /> },
];

const getGoalOutcomes = (t: (key: string, fallback: string) => string): { value: GoalOutcome; label: string }[] => [
  { value: 'met', label: t('serviceReferrals.goals.outcome.met', 'Goal Met') },
  { value: 'partially_met', label: t('serviceReferrals.goals.outcome.partiallyMet', 'Partially Met') },
  { value: 'not_met', label: t('serviceReferrals.goals.outcome.notMet', 'Not Met') },
  { value: 'ongoing', label: t('serviceReferrals.goals.outcome.ongoing', 'Ongoing') },
];

const getGoalCategories = (t: (key: string, fallback: string) => string): string[] => [
  t('serviceReferrals.goals.category.health', 'Health'),
  t('serviceReferrals.goals.category.housing', 'Housing'),
  t('serviceReferrals.goals.category.employment', 'Employment'),
  t('serviceReferrals.goals.category.education', 'Education'),
  t('serviceReferrals.goals.category.financial', 'Financial'),
  t('serviceReferrals.goals.category.social', 'Social'),
  t('serviceReferrals.goals.category.behavioral', 'Behavioral'),
  t('serviceReferrals.goals.category.functional', 'Functional'),
  t('serviceReferrals.goals.category.other', 'Other'),
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceGoalsTracker({
  referralId,
  onGoalUpdated,
  readOnly = false,
  className = '',
}: ServiceGoalsTrackerProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const GOAL_STATUSES = getGoalStatuses(t);
  const GOAL_OUTCOMES = getGoalOutcomes(t);
  const GOAL_CATEGORIES = getGoalCategories(t);

  // State
  const [goals, setGoals] = useState<ServiceGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Add Form State
  const [newGoal, setNewGoal] = useState<ServiceGoalCreate>({
    goal_description: '',
    goal_category: '',
    target_date: '',
  });

  // Edit Form State
  const [editData, setEditData] = useState<ServiceGoalUpdate>({});

  // Load goals
  useEffect(() => {
    loadGoals();
  }, [referralId]);

  const loadGoals = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getGoals(referralId);
      setGoals(data);
    } catch (err) {
      console.error('Failed to load goals:', err);
      setError(t('serviceReferrals.goals.errors.loadFailed', 'Failed to load goals'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoal.goal_description.trim()) {
      setError(t('serviceReferrals.goals.errors.descriptionRequired', 'Goal description is required'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await serviceReferralsAPI.addGoal(referralId, newGoal);
      setGoals([...goals, created]);
      setShowAddForm(false);
      setNewGoal({ goal_description: '', goal_category: '', target_date: '' });
    } catch (err) {
      console.error('Failed to add goal:', err);
      setError(t('serviceReferrals.goals.errors.addFailed', 'Failed to add goal'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (goal: ServiceGoal) => {
    setEditingGoalId(goal.id);
    setEditData({
      goal_description: goal.goal_description,
      goal_category: goal.goal_category,
      target_date: goal.target_date,
      status: goal.status,
      progress_percentage: goal.progress_percentage,
      progress_notes: goal.progress_notes,
      outcome: goal.outcome,
      outcome_notes: goal.outcome_notes,
    });
  };

  const handleSaveEdit = async (goalId: number) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await serviceReferralsAPI.updateGoal(referralId, goalId, editData);
      setGoals(goals.map((g) => (g.id === goalId ? updated : g)));
      setEditingGoalId(null);
      setEditData({});
      onGoalUpdated?.(updated);
    } catch (err) {
      console.error('Failed to update goal:', err);
      setError(t('serviceReferrals.goals.errors.updateFailed', 'Failed to update goal'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditData({});
  };

  const getStatusInfo = (status: GoalStatus) => {
    return GOAL_STATUSES.find((s) => s.value === status) || GOAL_STATUSES[0];
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('serviceReferrals.goals.noTargetDate', 'No target date');
    return fmt.medium(dateStr);
  };

  // Calculate overall progress
  const overallProgress = goals.length > 0
    ? Math.round(goals.reduce((sum, g) => sum + g.progress_percentage, 0) / goals.length)
    : 0;

  const achievedGoals = goals.filter((g) => g.status === 'achieved').length;
  const activeGoals = goals.filter((g) => g.status === 'active').length;

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5" />
              {t('serviceReferrals.goals.title', 'Service Goals')}
            </CardTitle>
            <CardDescription>
              {t('serviceReferrals.goals.description', 'Track and manage goals for this service')}
            </CardDescription>
          </div>
          {!readOnly && (
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              variant={showAddForm ? 'outline' : 'default'}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {showAddForm ? t('serviceReferrals.common.cancel', 'Cancel') : t('serviceReferrals.goals.addGoal', 'Add Goal')}
            </Button>
          )}
        </div>

        {/* Summary Stats */}
        {goals.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{goals.length}</div>
              <div className="text-xs text-blue-600">{t('serviceReferrals.goals.totalGoals', 'Total Goals')}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-700">{achievedGoals}</div>
              <div className="text-xs text-green-600">{t('serviceReferrals.goals.achieved', 'Achieved')}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-700">{overallProgress}%</div>
              <div className="text-xs text-purple-600">{t('serviceReferrals.goals.avgProgress', 'Avg Progress')}</div>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Add Goal Form */}
        {showAddForm && (
          <form onSubmit={handleAddGoal} className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
            <div>
              <Label htmlFor="goal_description">{t('serviceReferrals.goals.goalDescriptionLabel', 'Goal Description *')}</Label>
              <Textarea
                id="goal_description"
                value={newGoal.goal_description}
                onChange={(e) => setNewGoal({ ...newGoal, goal_description: e.target.value })}
                placeholder={t('serviceReferrals.goals.goalDescriptionPlaceholder', 'Describe the goal in specific, measurable terms...')}
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="goal_category">{t('serviceReferrals.goals.category', 'Category')}</Label>
                <select
                  id="goal_category"
                  value={newGoal.goal_category}
                  onChange={(e) => setNewGoal({ ...newGoal, goal_category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">{t('serviceReferrals.goals.selectCategory', 'Select category...')}</option>
                  {GOAL_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="target_date">{t('serviceReferrals.goals.targetDate', 'Target Date')}</Label>
                <Input
                  id="target_date"
                  type="date"
                  value={newGoal.target_date}
                  onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                {t('serviceReferrals.common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('serviceReferrals.goals.addGoal', 'Add Goal')}
              </Button>
            </div>
          </form>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Empty State */}
        {!loading && goals.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>{t('serviceReferrals.goals.noGoals', 'No goals defined yet')}</p>
            {!readOnly && (
              <Button variant="link" onClick={() => setShowAddForm(true)}>
                {t('serviceReferrals.goals.addFirstGoal', 'Add the first goal')}
              </Button>
            )}
          </div>
        )}

        {/* Goals List */}
        {!loading && goals.length > 0 && (
          <div className="space-y-4">
            {goals.map((goal) => {
              const statusInfo = getStatusInfo(goal.status);
              const isEditing = editingGoalId === goal.id;
              const isOverdue = goal.target_date && new Date(goal.target_date) < new Date() && goal.status === 'active';

              return (
                <div
                  key={goal.id}
                  className={`border rounded-lg p-4 ${isEditing ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  {/* View Mode */}
                  {!isEditing && (
                    <>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`
                              ${statusInfo.color === 'blue' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
                              ${statusInfo.color === 'green' ? 'border-green-300 text-green-700 bg-green-50' : ''}
                              ${statusInfo.color === 'amber' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}
                              ${statusInfo.color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
                              ${statusInfo.color === 'purple' ? 'border-purple-300 text-purple-700 bg-purple-50' : ''}
                            `}
                          >
                            {statusInfo.icon}
                            <span className="ml-1">{statusInfo.label}</span>
                          </Badge>
                          {goal.goal_category && (
                            <Badge variant="outline" className="text-xs">
                              {goal.goal_category}
                            </Badge>
                          )}
                          {isOverdue && (
                            <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50 text-xs">
                              {t('serviceReferrals.goals.overdue', 'Overdue')}
                            </Badge>
                          )}
                        </div>
                        {!readOnly && (
                          <Button variant="ghost" size="sm" onClick={() => handleStartEdit(goal)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <p className="text-sm font-medium mb-2">{goal.goal_description}</p>

                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {t('serviceReferrals.goals.target', 'Target')}: {formatDate(goal.target_date)}
                        </div>
                        <div>{t('serviceReferrals.goals.goalNumber', 'Goal')} #{goal.goal_number}</div>
                      </div>

                      {/* Progress Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">{t('serviceReferrals.goals.progress', 'Progress')}</span>
                          <span className="font-medium">{goal.progress_percentage}%</span>
                        </div>
                        <Progress value={goal.progress_percentage} className="h-2" />
                      </div>

                      {goal.progress_notes && (
                        <p className="mt-2 text-xs text-gray-500 italic">{goal.progress_notes}</p>
                      )}

                      {goal.outcome && (
                        <div className="mt-2 pt-2 border-t">
                          <Badge
                            variant="outline"
                            className={`
                              ${goal.outcome === 'met' ? 'border-green-300 text-green-700 bg-green-50' : ''}
                              ${goal.outcome === 'partially_met' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}
                              ${goal.outcome === 'not_met' ? 'border-red-300 text-red-700 bg-red-50' : ''}
                              ${goal.outcome === 'ongoing' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
                            `}
                          >
                            {t('serviceReferrals.goals.outcomeLabel', 'Outcome')}: {GOAL_OUTCOMES.find((o) => o.value === goal.outcome)?.label}
                          </Badge>
                          {goal.outcome_notes && (
                            <p className="mt-1 text-xs text-gray-500">{goal.outcome_notes}</p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Edit Mode */}
                  {isEditing && (
                    <div className="space-y-4">
                      <div>
                        <Label>{t('serviceReferrals.goals.goalDescription', 'Goal Description')}</Label>
                        <Textarea
                          value={editData.goal_description || ''}
                          onChange={(e) => setEditData({ ...editData, goal_description: e.target.value })}
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t('serviceReferrals.goals.status', 'Status')}</Label>
                          <select
                            value={editData.status || 'active'}
                            onChange={(e) => setEditData({ ...editData, status: e.target.value as GoalStatus })}
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            {GOAL_STATUSES.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label>{t('serviceReferrals.goals.progressPercent', 'Progress %')}</Label>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={editData.progress_percentage || 0}
                            onChange={(e) => setEditData({ ...editData, progress_percentage: parseInt(e.target.value) || 0 })}
                          />
                        </div>

                        <div>
                          <Label>{t('serviceReferrals.goals.category', 'Category')}</Label>
                          <select
                            value={editData.goal_category || ''}
                            onChange={(e) => setEditData({ ...editData, goal_category: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            <option value="">{t('serviceReferrals.common.select', 'Select...')}</option>
                            {GOAL_CATEGORIES.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label>{t('serviceReferrals.goals.targetDate', 'Target Date')}</Label>
                          <Input
                            type="date"
                            value={editData.target_date || ''}
                            onChange={(e) => setEditData({ ...editData, target_date: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>{t('serviceReferrals.goals.progressNotes', 'Progress Notes')}</Label>
                        <Textarea
                          value={editData.progress_notes || ''}
                          onChange={(e) => setEditData({ ...editData, progress_notes: e.target.value })}
                          rows={2}
                          placeholder={t('serviceReferrals.goals.progressNotesPlaceholder', 'Notes on progress...')}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t('serviceReferrals.goals.outcome', 'Outcome')}</Label>
                          <select
                            value={editData.outcome || ''}
                            onChange={(e) => setEditData({ ...editData, outcome: e.target.value as GoalOutcome })}
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            <option value="">{t('serviceReferrals.goals.selectOutcome', 'Select outcome...')}</option>
                            {GOAL_OUTCOMES.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label>{t('serviceReferrals.goals.outcomeNotes', 'Outcome Notes')}</Label>
                          <Input
                            value={editData.outcome_notes || ''}
                            onChange={(e) => setEditData({ ...editData, outcome_notes: e.target.value })}
                            placeholder={t('serviceReferrals.goals.outcomeNotesPlaceholder', 'Outcome details...')}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                          <X className="w-4 h-4 mr-1" />
                          {t('serviceReferrals.common.cancel', 'Cancel')}
                        </Button>
                        <Button size="sm" onClick={() => handleSaveEdit(goal.id)} disabled={saving}>
                          {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                          <Check className="w-4 h-4 mr-1" />
                          {t('serviceReferrals.common.save', 'Save')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceGoalsTracker;
